import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import {
    RiskDirectoryForUserListDto,
    StandardAccessRight,
} from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { WebRoute } from "routes";
import { canViewRiskDirectory } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    RISK_DIRECTORIES_LIST_COLUMN_IDS,
    RISK_DIRECTORIES_TABLE_STATE_STORAGE_KEY,
    riskDirectoriesListColumnFiltersCodecMap,
    riskDirectoriesListTableDefaultValues,
} from "./consts";
export interface RiskDirectoryRow {
    id: number;
    title: string;
    description: string | null;
    accessRight: StandardAccessRight;
}

export function useUserRiskDirectoriesListTable(
    data: RiskDirectoryForUserListDto
) {
    const { t } = useTranslation();

    const tableData = useMemo<RiskDirectoryRow[]>(
        () =>
            data.content.map(
                ({ resource: { riskDirectory, accessRight } }) => ({
                    id: riskDirectory.id,
                    title: riskDirectory.title,
                    description: riskDirectory.description ?? null,
                    accessRight,
                })
            ),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        RISK_DIRECTORIES_TABLE_STATE_STORAGE_KEY,
        riskDirectoriesListTableDefaultValues,
        riskDirectoriesListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<RiskDirectoryRow>(),
        getSortedRowModel: getSortedRowModel<RiskDirectoryRow>(),
        getFilteredRowModel: getFilteredRowModel<RiskDirectoryRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<RiskDirectoryRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<RiskDirectoryRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (column: Column<RiskDirectoryRow>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: RISK_DIRECTORIES_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<RiskDirectoryRow>();
function buildColumns(
    t: TFunction,
    riskDirectories: RiskDirectoryForUserListDto
) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        indeterminate={info.table.getIsSomePageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
        }),
        columnHelper.accessor((row) => row.title, {
            id: RISK_DIRECTORIES_LIST_COLUMN_IDS.TITLE,
            cell: (info) => {
                const row = info.row.original;
                const actions = riskDirectories.content.find(
                    (dto) => dto.resource.riskDirectory.id === row.id
                )?.actions;
                const hasAccess = actions
                    ? canViewRiskDirectory(actions)
                    : false;
                return (
                    <ProtectedLink
                        to={`${WebRoute.RISKS}/${row.id}/structure`}
                        hasAccess={hasAccess}
                    >
                        {row.title}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<RiskDirectoryRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        RISK_DIRECTORIES_LIST_COLUMN_IDS.TITLE,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_RiskDirectories_Title"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.description ?? "", {
            id: RISK_DIRECTORIES_LIST_COLUMN_IDS.DESCRIPTION,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            header: () => t("AccessAndAssignment_RiskDirectories_Description"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.accessRight, {
            id: RISK_DIRECTORIES_LIST_COLUMN_IDS.ACCESS_RIGHT,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<RiskDirectoryRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        RISK_DIRECTORIES_LIST_COLUMN_IDS.ACCESS_RIGHT,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_RiskDirectories_AccessRight"),
            size: TST_COLUMN_SIZE.M,
        }),
    ];
}

type RiskDirectoriesListColumnFilters = ZodCodecMapToType<
    typeof riskDirectoriesListColumnFiltersCodecMap
>;

type RiskDirectoriesFilterableColumnId = keyof RiskDirectoriesListColumnFilters;

function getFilterComponent(t: TFunction, column: Column<RiskDirectoryRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<RiskDirectoriesListColumnFilters>(column);
    const columnId = column.id as RiskDirectoriesFilterableColumnId;

    switch (columnId) {
        case RISK_DIRECTORIES_LIST_COLUMN_IDS.TITLE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((riskDirectory) => ({
                label: riskDirectory,
                value: riskDirectory,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t("AccessAndAssignment_RiskDirectories_Title")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case RISK_DIRECTORIES_LIST_COLUMN_IDS.ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((accessRight) => ({
                label: accessRight,
                value: accessRight,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "AccessAndAssignment_RiskDirectories_AccessRight"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
